import * as React from 'react'
import {
  Box,
  Button,
  Container,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from '@mui/material'
import { FormInputText } from '../../../components/FormInputText'
import { FormInputSelect } from '../../../components/FormInputSelect'
import { FormInputDatePicker } from '../../../components/FormInputDatePicker'
import LoadingButton from '@mui/lab/LoadingButton'
import Dialog from '@mui/material/Dialog'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import { athleteSchema } from '../validators'
import { useEffect, useState } from 'react'
import { GenreDB } from '../../../domain/db/GenreOptions'
import { useTranslation } from 'react-i18next'
import api from '../../../services/api'
import dayjs from 'dayjs'
import { ProfessionalAthlete } from 'models/interfaces/professional-athlete.interface'
import { Athlete } from 'models/interfaces/athlete.interface'

type Props = {
  isVisible: boolean
  onClose: () => void
  loading: boolean
  handleAddAthlete: (athlete: Athlete) => Promise<void>
  professionalAthlete?: ProfessionalAthlete
}

export const AthleteFormModal = ({ isVisible, onClose, loading, handleAddAthlete, professionalAthlete }: Props) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm<Athlete>({
    resolver: yupResolver(athleteSchema)
  })
  const [disableFields, setDisableFields] = useState(true)
  useEffect(() => {
    if (isVisible) {
      reset()
      !professionalAthlete?.athlete?.userVerifiedAt ? setDisableFields(false) : setDisableFields(true)
      professionalAthlete?.athlete?.id ? setValue('id', professionalAthlete?.athlete?.id) : setValue('id', '')
    }
  }, [isVisible])

  const handleFindAthlete = async (event: any) => {
    if (professionalAthlete) {
      return
    }
    const email = event.target.value
    setDisableFields(true)
    if (!email) return
    try {
      const athleteData = await api.get(`athletes/${email}`)
      if (athleteData?.data.userVerifiedAt === null) {
        setDisableFields(false)
      }
      const birthday = dayjs(athleteData.data.birthday)
      setValue('id', athleteData.data.id)
      setValue('name', athleteData.data.name)
      setValue('surname', athleteData.data.surname)
      setValue('genre', athleteData.data.genre)
      setValue('birthday', birthday as unknown as string)
      setValue('whatsapp', athleteData.data?.whatsapp)
    } catch (error) {
      setDisableFields(false)
      setValue('id', '')
      setValue('name', '')
      setValue('surname', '')
      setValue('genre', '')
      setValue('birthday', '')
      setValue('whatsapp', '')
    }
  }

  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      open={isVisible}
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
    >
      <DialogTitle>{(professionalAthlete ? t('athlete.title.editAthlete') : t('athlete.title.addAthlete'))}</DialogTitle>
      <DialogContent>
        <Container component="main">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box component="form" noValidate sx={{ my: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormInputText
                    autoFocus
                    name="email"
                    type="email"
                    required
                    control={control}
                    value={professionalAthlete?.athlete?.email}
                    onBlur={ handleFindAthlete }
                    label={`${t('athlete.field.email')}`}
                    errorMessage={errors.email?.message}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInputText
                    name="name"
                    required
                    disabled={disableFields}
                    value={professionalAthlete?.athlete?.name}
                    control={control}
                    label={`${t('athlete.field.name')}`}
                    errorMessage={errors.name?.message}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInputText
                    name="surname"
                    required
                    control={control}
                    disabled={disableFields}
                    value={professionalAthlete?.athlete?.surname}
                    label={`${t('athlete.field.surname')}`}
                    errorMessage={errors.surname?.message}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInputSelect
                    name="genre"
                    required={true}
                    control={control}
                    disabled={disableFields}
                    value={professionalAthlete?.athlete?.genre}
                    label={`${t('athlete.field.genre')}`}
                    items={GenreDB}
                    errorMessage={errors.genre?.message}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInputDatePicker
                    name="birthday"
                    required={true}
                    control={control}
                    disabled={disableFields}
                    value={professionalAthlete?.athlete?.birthday}
                    label={`${t('athlete.field.birthday')}`}
                    errorMessage={errors.birthday?.message}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormInputText
                    name="whatsapp"
                    required
                    control={control}
                    label={`${t('athlete.field.whatsapp')}`}
                    disabled={disableFields}
                    value={professionalAthlete?.athlete?.whatsapp}
                    errorMessage={errors.whatsapp?.message}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Container>
      </DialogContent>
      <DialogActions>
        <LoadingButton loading={loading} onClick={handleSubmit(handleAddAthlete)} variant="contained">
          {t('general.btn.save')}
        </LoadingButton>
        <Button variant="contained" color="secondary" onClick={onClose}>
          {t('general.btn.dismiss')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
